import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Activity, ExternalLink, Maximize2, Minimize2, RefreshCw, ShieldCheck } from "lucide-react";
import { Navigate } from "react-router-dom";

import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";

const SHOP_ROLES = ["owner", "project_manager", "office_admin", "employee"];

const configuredUrl = () => {
  const raw = process.env.REACT_APP_CNC_ROUTER_URL;
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    return parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
};

export default function CncRouterStationPage() {
  const { user } = useContext(AuthContext);
  const workspaceRef = useRef(null);
  const [frameVersion, setFrameVersion] = useState(0);
  const [frameLoading, setFrameLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const workspaceUrl = useMemo(configuredUrl, []);

  useEffect(() => {
    const syncFullscreen = () => setFullscreen(document.fullscreenElement === workspaceRef.current);
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () => document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  if (!SHOP_ROLES.includes(user?.role)) return <Navigate to="/dashboard" replace />;

  const reloadFrame = () => {
    setFrameLoading(true);
    setFrameVersion(version => version + 1);
  };

  const toggleFullscreen = async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    else if (workspaceRef.current?.requestFullscreen) await workspaceRef.current.requestFullscreen();
  };

  return (
    <section ref={workspaceRef} className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-slate-950 text-slate-100" data-testid="cnc-router-station-workspace">
      <header className="flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-800 bg-slate-950 px-3 py-2 sm:px-5" data-testid="cnc-router-station-toolbar">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-cyan-400/25 bg-cyan-400/10"><Activity className="h-5 w-5 text-cyan-300" /></div>
          <div className="min-w-0">
            <h1 className="truncate text-base font-bold text-white sm:text-lg" data-testid="cnc-router-station-title">CNC Router Station</h1>
            <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="cnc-status-indicator"><span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" /></span><span className="truncate">Direct external workspace</span></div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button type="button" variant="outline" size="sm" onClick={reloadFrame} disabled={!workspaceUrl} className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white" data-testid="cnc-reload-iframe-button"><RefreshCw className={`h-4 w-4 ${frameLoading ? "animate-spin" : ""}`} /><span className="hidden sm:inline">Reload</span></Button>
          <Button type="button" variant="outline" size="sm" onClick={toggleFullscreen} className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white" aria-label={fullscreen ? "Exit full screen" : "Enter full screen"} data-testid="cnc-fullscreen-toggle-button">{fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}<span className="hidden md:inline">{fullscreen ? "Exit full screen" : "Full screen"}</span></Button>
          {workspaceUrl && <Button asChild size="sm" className="bg-cyan-500 text-slate-950 hover:bg-cyan-400"><a href={workspaceUrl} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" data-testid="cnc-open-external-button"><ExternalLink className="h-4 w-4" /><span className="hidden sm:inline">Open externally</span><span className="sr-only sm:hidden">Open CNC Router Station externally</span></a></Button>}
        </div>
      </header>

      <div className="relative min-h-0 flex-1 overflow-hidden bg-slate-900" data-testid="cnc-iframe-container">
        {!workspaceUrl ? (
          <div className="flex h-full items-center justify-center p-6 text-center" data-testid="cnc-iframe-configuration-error"><div><ShieldCheck className="mx-auto h-9 w-9 text-amber-400" /><p className="mt-3 font-semibold text-white">Shop workspace is not configured</p><p className="mt-1 text-sm text-slate-400">Ask an administrator to set the secure HTTPS workspace URL.</p></div></div>
        ) : (
          <>
            {frameLoading && <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-slate-950" data-testid="cnc-iframe-loading"><RefreshCw className="h-6 w-6 animate-spin text-cyan-300" /></div>}
            <iframe
              key={frameVersion}
              src={workspaceUrl}
              title="CNC Router Station Production Workspace"
              className="block h-full w-full border-0 bg-white"
              onLoad={() => setFrameLoading(false)}
              loading="eager"
              referrerPolicy="no-referrer"
              allow="clipboard-write; fullscreen"
              sandbox="allow-downloads allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts"
              data-testid="cnc-production-iframe"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center px-3" data-testid="cnc-iframe-fallback-card"><p className="pointer-events-auto rounded-md border border-slate-700 bg-slate-950/95 px-3 py-2 text-center text-xs text-slate-300 shadow-lg">Blank or blocked? <a href={workspaceUrl} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" className="font-semibold text-cyan-300 hover:text-cyan-200" data-testid="cnc-fallback-external-link">Open the station in a new tab.</a></p></div>
          </>
        )}
      </div>
    </section>
  );
}